import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Plus, Save, Trash2, RotateCcw, Loader2, Search, Info, Eye, Lock, FileText, Upload, X, Sparkles, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'electricite', label: 'Électricité', emoji: '⚡' },
  { id: 'plomberie', label: 'Plomberie / SDB', emoji: '🚿' },
  { id: 'sols', label: 'Sols', emoji: '🧱' },
  { id: 'peinture', label: 'Peinture', emoji: '🎨' },
  { id: 'cuisine', label: 'Cuisine', emoji: '🍳' },
  { id: 'menuiseries_int', label: 'Menuiseries Intérieures', emoji: '🚪' },
  { id: 'menuiseries_ext', label: 'Menuiseries Extérieures', emoji: '🪟' },
  { id: 'serrurerie', label: 'Serrurerie', emoji: '🔐' },
  { id: 'toiture', label: 'Toiture', emoji: '🏠' },
  { id: 'facade', label: 'Façade', emoji: '🧱' },
  { id: 'isolation', label: 'Isolation', emoji: '🧊' },
  { id: 'chauffage', label: 'Chauffage', emoji: '🔥' },
  { id: 'demolition', label: 'Démolition', emoji: '🔨' },
  { id: 'maconnerie', label: 'Maçonnerie', emoji: '🧱' },
  { id: 'diagnostics', label: 'Diagnostics', emoji: '📋' },
  { id: 'frais', label: 'Frais & Honoraires', emoji: '💼' },
  { id: 'assurances', label: 'Assurances', emoji: '🛡️' },
];

const UNITS = ['m²', 'ml', 'forfait', 'unité', 'm³', '%', 'heure', 'jour'];

export default function ProductCatalogTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [editing, setEditing] = useState(null); // full product being edited (modal)
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [generatingAuto, setGeneratingAuto] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  const token = () => localStorage.getItem('token');

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/product-catalog`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error('Erreur chargement catalogue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (filterCat !== 'all' && p.category !== filterCat) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [products, search, filterCat]);

  const openNew = () => {
    setEditing({
      category: filterCat === 'all' ? 'sols' : filterCat,
      name: '',
      unit: 'm²',
      prix_fourniture: 0,
      prix_pose: 0,
      description: '',
      specifications: '',
      notes_internes: '',
      active: true,
    });
    setIsNew(true);
  };

  const openEdit = (p) => {
    setEditing({ ...p });
    setIsNew(false);
  };

  const saveEdit = async () => {
    if (!editing.name || !editing.category || !editing.unit) {
      toast.error('Nom, catégorie et unité requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: editing.category,
        name: editing.name,
        unit: editing.unit,
        prix_fourniture: parseFloat(editing.prix_fourniture) || 0,
        prix_pose: parseFloat(editing.prix_pose) || 0,
        description: editing.description || '',
        specifications: editing.specifications || '',
        notes_internes: editing.notes_internes || '',
        active: editing.active !== false,
      };
      if (isNew) {
        const res = await axios.post(`${API}/api/admin/product-catalog`, payload, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        setProducts(prev => [...prev, res.data]);
      } else {
        await axios.put(`${API}/api/admin/product-catalog/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        setProducts(prev => prev.map(p => p.id === editing.id ? { ...p, ...payload } : p));
      }
      setEditing(null);
      toast.success(isNew ? 'Produit ajouté' : 'Produit modifié');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/product-catalog/${p.id}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setProducts(prev => prev.filter(x => x.id !== p.id));
      toast.success('Supprimé');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/api/admin/product-catalog/${p.id}`, { active: !p.active }, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, active: !x.active } : x));
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Remettre le catalogue par défaut ? Cela supprimera TOUS tes produits personnalisés.')) return;
    try {
      await axios.post(`${API}/api/admin/product-catalog/reset-defaults`, {}, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      await fetchProducts();
      toast.success('Catalogue réinitialisé');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState('upsert');
  const [importReport, setImportReport] = useState(null);
  const fileInputRef = React.useRef(null);

  const downloadCsvTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/product-catalog/csv-template`, {
        headers: { Authorization: `Bearer ${token()}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_catalogue_travaux.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur téléchargement modèle');
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.csv')) {
      toast.error('Seuls les fichiers CSV sont acceptés');
      return;
    }
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/api/admin/product-catalog/import-csv?mode=${importMode}`,
        fd,
        { headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' } }
      );
      setImportReport(res.data);
      await fetchProducts();
      toast.success(`Import réussi : +${res.data.inserted} · ~${res.data.updated} · ignorés ${res.data.skipped}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const uploadPdfForEditing = async (file) => {
    if (!editing || !editing.id) {
      toast.error('Enregistre d\'abord le produit avant d\'ajouter une fiche PDF');
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/api/admin/product-catalog/${editing.id}/upload-pdf`,
        fd,
        { headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' } }
      );
      const { fiche_pdf_url, fiche_pdf_filename } = res.data;
      setEditing(prev => prev ? { ...prev, fiche_pdf_url, fiche_pdf_filename } : prev);
      setProducts(prev => prev.map(x => x.id === editing.id ? { ...x, fiche_pdf_url, fiche_pdf_filename } : x));
      toast.success('Fiche PDF uploadée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const removePdfForEditing = async () => {
    if (!editing || !editing.id) return;
    if (!window.confirm('Supprimer la fiche PDF de ce produit ?')) return;
    try {
      await axios.delete(`${API}/api/admin/product-catalog/${editing.id}/pdf`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setEditing(prev => prev ? { ...prev, fiche_pdf_url: '', fiche_pdf_filename: '', fiche_pdf_type: '' } : prev);
      setProducts(prev => prev.map(x => x.id === editing.id ? { ...x, fiche_pdf_url: '', fiche_pdf_filename: '', fiche_pdf_type: '' } : x));
      toast.success('Fiche PDF supprimée');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const generateAutoForEditing = async () => {
    if (!editing || !editing.id) {
      toast.error('Enregistre d\'abord le produit');
      return;
    }
    if (editing.fiche_pdf_type === 'manual') {
      if (!window.confirm('Cette fiche est la VRAIE fiche constructeur (📎 Officielle). La remplacer par une fiche auto générée ?')) return;
    } else if (editing.fiche_pdf_url) {
      if (!window.confirm('Régénérer la fiche auto ? L\'actuelle sera écrasée.')) return;
    }
    setGeneratingAuto(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/product-catalog/${editing.id}/generate-auto-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const { fiche_pdf_url, fiche_pdf_filename, fiche_pdf_type } = res.data;
      setEditing(prev => prev ? { ...prev, fiche_pdf_url, fiche_pdf_filename, fiche_pdf_type } : prev);
      setProducts(prev => prev.map(x => x.id === editing.id ? { ...x, fiche_pdf_url, fiche_pdf_filename, fiche_pdf_type } : x));
      toast.success('Fiche auto générée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération fiche auto');
    } finally {
      setGeneratingAuto(false);
    }
  };

  const bulkGenerateAuto = async () => {
    const withoutFiche = products.filter(p => !p.fiche_pdf_url).length;
    if (withoutFiche === 0) {
      toast.info('Tous les produits ont déjà une fiche');
      return;
    }
    if (!window.confirm(`Générer une fiche auto pour les ${withoutFiche} produit(s) sans fiche ?\n\n✅ Les fiches officielles uploadées (📎) ne seront PAS touchées\n✅ Les fiches auto existantes ne seront PAS écrasées`)) return;
    setBulkGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/product-catalog/generate-auto-pdf-bulk`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const { processed, skipped_manual, skipped_auto, failed } = res.data;
      toast.success(`✅ ${processed} fiche(s) auto générée(s) · 📎 ${skipped_manual} officielle(s) préservée(s) · 🤖 ${skipped_auto} auto existante(s) préservée(s)${failed ? ` · ❌ ${failed} échec(s)` : ''}`);
      await fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération en masse');
    } finally {
      setBulkGenerating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-4" data-testid="product-catalog-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-navy">Catalogue Prix Travaux</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Base de prix utilisée par le Dashboard Expert (autocomplete). Pose + Fourniture séparés.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau produit
          </Button>
          <Button
            onClick={bulkGenerateAuto}
            disabled={bulkGenerating}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="bulk-generate-auto-btn"
          >
            {bulkGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {bulkGenerating ? 'Génération...' : '🤖 Fiches auto (produits sans PDF)'}
          </Button>
          <Button variant="outline" onClick={resetDefaults} className="border-red-300 text-red-700 hover:bg-red-50">
            <RotateCcw className="w-4 h-4 mr-2" /> Remettre par défaut
          </Button>
        </div>
      </div>

      {/* Import CSV */}
      <Card className="bg-amber-50 border-amber-200" data-testid="csv-import-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Upload className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900 text-sm">Import tarifs fournisseurs (CSV)</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Colonnes : <code>category,name,unit,prix_fourniture,prix_pose,description</code> · Sép. <code>,</code> ou <code>;</code> · UTF-8
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              className="border rounded h-9 px-2 text-sm bg-white"
              data-testid="csv-import-mode"
            >
              <option value="upsert">Upsert (MAJ si existe, sinon crée)</option>
              <option value="append">Append (ignore doublons)</option>
              <option value="replace_category">Remplacer toute la catégorie filtrée</option>
            </select>
            <Button
              variant="outline"
              onClick={downloadCsvTemplate}
              className="border-amber-400 text-amber-800 hover:bg-amber-100 h-9"
              data-testid="download-csv-template-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> Modèle CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => importCsv(e.target.files?.[0])}
              className="hidden"
              data-testid="csv-import-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="bg-amber-600 hover:bg-amber-700 text-white h-9"
              data-testid="import-csv-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? 'Import...' : 'Importer CSV'}
            </Button>
          </div>
          {importReport && (
            <div className="text-xs bg-white rounded p-2 border border-amber-300" data-testid="csv-import-report">
              <div className="font-semibold text-amber-900">Rapport d'import :</div>
              <div>✅ Insérés : <b>{importReport.inserted}</b> · 🔁 Mis à jour : <b>{importReport.updated}</b> · ⚠️ Ignorés : <b>{importReport.skipped}</b></div>
              {importReport.errors && importReport.errors.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-red-700">
                  {importReport.errors.slice(0, 8).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-3 flex items-start gap-2 text-sm text-blue-900">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Fourniture</strong> = matériau seul · <strong>Pose</strong> = main d'œuvre seule · <strong>Total</strong> = F + P.
            L'expert choisit le mode de facturation dossier par dossier (F+P, F seule, P seule, ou séparé).
            Les <strong>notes internes</strong> (🔒) restent visibles uniquement par toi.
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un produit..." className="pl-9 h-9" data-testid="catalog-search" />
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="border rounded h-9 px-3 text-sm"
            data-testid="catalog-category-filter"
          >
            <option value="all">Toutes catégories</option>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
          </select>
          <span className="text-sm text-muted-foreground">{filtered.length} / {products.length}</span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-3 py-2 text-left">Catégorie</th>
                <th className="px-3 py-2 text-left">Nom du produit</th>
                <th className="px-3 py-2 text-center w-16">Unité</th>
                <th className="px-3 py-2 text-right w-24">Fourniture</th>
                <th className="px-3 py-2 text-right w-24">Pose</th>
                <th className="px-3 py-2 text-right w-24">Total</th>
                <th className="px-3 py-2 text-center w-16">Actif</th>
                <th className="px-3 py-2 text-right w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const cat = CATEGORIES.find(c => c.id === p.category);
                return (
                  <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`product-row-${p.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{cat?.emoji} {cat?.label || p.category}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy flex items-center gap-1.5 flex-wrap">
                        {p.name}
                        {p.fiche_pdf_url && p.fiche_pdf_type === 'manual' && (
                          <a
                            href={p.fiche_pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200"
                            title={p.fiche_pdf_filename || 'Fiche officielle'}
                            data-testid={`table-pdf-${p.id}`}
                          >
                            📎 Officielle
                          </a>
                        )}
                        {p.fiche_pdf_url && p.fiche_pdf_type === 'auto' && (
                          <a
                            href={p.fiche_pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300 hover:bg-indigo-200"
                            title={p.fiche_pdf_filename || 'Fiche auto'}
                            data-testid={`table-pdf-${p.id}`}
                          >
                            🤖 Auto
                          </a>
                        )}
                        {p.fiche_pdf_url && !p.fiche_pdf_type && (
                          <a
                            href={p.fiche_pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-red-600 hover:text-red-700 text-xs"
                            title={p.fiche_pdf_filename || 'Fiche PDF'}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                      {p.notes_internes && <div className="text-xs text-amber-700 flex items-center gap-1 mt-0.5"><Lock className="w-3 h-3" /> Notes internes</div>}
                    </td>
                    <td className="px-3 py-2 text-center text-xs">{p.unit}</td>
                    <td className="px-3 py-2 text-right text-xs">{p.prix_fourniture?.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right text-xs">{p.prix_pose?.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-700">{((p.prix_fourniture || 0) + (p.prix_pose || 0)).toFixed(2)} €</td>
                    <td className="px-3 py-2 text-center"><Switch checked={p.active} onCheckedChange={() => toggleActive(p)} /></td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>Modifier</Button>
                        <Button size="sm" variant="outline" onClick={() => deleteProduct(p)} className="border-red-300 text-red-700"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Aucun produit trouvé</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit / Create Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <Card className="max-w-2xl w-full bg-white max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-navy">{isNew ? 'Nouveau produit' : 'Modifier le produit'}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold mb-1 block">Catégorie *</label>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="border rounded w-full h-10 px-2" data-testid="modal-category">
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold mb-1 block">Unité *</label>
                  <select value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} className="border rounded w-full h-10 px-2" data-testid="modal-unit">
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold mb-1 block">Nom *</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ex: Parquet flottant contrecollé" data-testid="modal-name" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold mb-1 block">Prix Fourniture (€)</label>
                  <Input type="number" step="0.01" value={editing.prix_fourniture} onChange={(e) => setEditing({ ...editing, prix_fourniture: e.target.value })} data-testid="modal-fourniture" />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-1 block">Prix Pose (€)</label>
                  <Input type="number" step="0.01" value={editing.prix_pose} onChange={(e) => setEditing({ ...editing, prix_pose: e.target.value })} data-testid="modal-pose" />
                </div>
              </div>

              <div className="text-sm text-emerald-700 font-semibold">
                Total : {((parseFloat(editing.prix_fourniture) || 0) + (parseFloat(editing.prix_pose) || 0)).toFixed(2)} € / {editing.unit}
              </div>

              <div>
                <label className="text-sm font-semibold mb-1 flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Description (visible client si coché au PDF)
                </label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} placeholder="Ex: Parquet contrecollé 8mm chêne, pose flottante sur sous-couche" data-testid="modal-description" />
              </div>

              <div>
                <label className="text-sm font-semibold mb-1 flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Spécifications techniques (visible expert + client si coché PDF)
                </label>
                <Textarea value={editing.specifications} onChange={(e) => setEditing({ ...editing, specifications: e.target.value })} rows={2} placeholder="Ex: Épaisseur 8mm, classe 31, compatible chauffage sol" data-testid="modal-specs" />
              </div>

              <div>
                <label className="text-sm font-semibold mb-1 flex items-center gap-2 text-amber-700">
                  <Lock className="w-3.5 h-3.5" /> Notes internes (visible UNIQUEMENT par toi - Admin)
                </label>
                <Textarea value={editing.notes_internes} onChange={(e) => setEditing({ ...editing, notes_internes: e.target.value })} rows={3} placeholder="Ex: Leroy Merlin 28€/m² en promo. Jean-Marc (06 XX XX) pose 17€/m²." className="bg-amber-50" data-testid="modal-notes" />
              </div>

              <div className="border rounded-lg p-3 bg-red-50/50 border-red-200">
                <label className="text-sm font-semibold mb-2 flex items-center gap-2 text-red-700">
                  <FileText className="w-4 h-4" /> Fiche technique PDF (optionnel)
                </label>
                {editing.id ? (
                  <>
                    {editing.fiche_pdf_url ? (
                      <div className="bg-white rounded border border-red-200 p-2 mb-2">
                        <div className="flex items-center justify-between gap-2">
                          <a href={editing.fiche_pdf_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-red-700 hover:text-red-800 text-sm truncate flex-1 min-w-0">
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{editing.fiche_pdf_filename || 'Fiche PDF'}</span>
                            {editing.fiche_pdf_type === 'manual' && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">📎 Officielle</span>
                            )}
                            {editing.fiche_pdf_type === 'auto' && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300">🤖 Auto</span>
                            )}
                          </a>
                          <button type="button" onClick={removePdfForEditing} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50" data-testid="modal-pdf-remove">
                            <X className="w-3 h-3" /> Supprimer
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-red-700/70 italic mb-2">Aucune fiche PDF pour ce produit.</div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={generateAutoForEditing}
                        disabled={generatingAuto}
                        className="flex items-center justify-center gap-1.5 border-2 border-dashed border-indigo-300 rounded py-2.5 text-indigo-700 hover:bg-indigo-50 text-sm font-medium disabled:opacity-50"
                        data-testid="modal-generate-auto-btn"
                      >
                        {generatingAuto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {editing.fiche_pdf_url ? 'Régénérer auto' : '🤖 Générer fiche auto'}
                      </button>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadPdfForEditing(e.target.files[0])}
                          data-testid="modal-pdf-upload-input"
                        />
                        <div className={`flex items-center justify-center gap-1.5 border-2 border-dashed border-emerald-300 rounded py-2.5 text-emerald-700 hover:bg-emerald-50 text-sm font-medium ${uploadingPdf ? 'opacity-50' : ''}`}>
                          {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          {editing.fiche_pdf_url ? 'Remplacer par vraie fiche' : '📎 Uploader vraie fiche'}
                        </div>
                      </label>
                    </div>
                    <div className="mt-2 text-[11px] text-gray-600 space-y-0.5">
                      <p><strong className="text-indigo-700">🤖 Fiche auto</strong> = générée par le système avec votre logo L'Œil du Marchand, prix et description.</p>
                      <p><strong className="text-emerald-700">📎 Vraie fiche</strong> = PDF officiel constructeur (Schüco, Daikin, etc.) que vous téléchargez du site fabricant.</p>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-red-700/80 italic">Enregistre d'abord le produit, puis reviens pour gérer la fiche PDF.</p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={() => setEditing(null)} variant="outline" className="flex-1">Annuler</Button>
                <Button onClick={saveEdit} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="modal-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
